import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PAYMENT_EMAIL } from "@/lib/brand";
import {
  ShoppingCart,
  Trash2,
  ArrowRight,
  Truck,
  MapPin,
  CreditCard,
  Mail,
  CheckCircle2,
  Lock,
  AlertCircle,
} from "lucide-react";

const STEPS = ["cart", "shipping", "payment"];

export default function CheckoutPage() {
  const { items, subtotal, remove, setQuantity, clear } = useCart();
  const { user, isCustomer } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState("cart");
  const [settings, setSettings] = useState({
    flat_shipping_rate: 15.0,
    pickup_available: true,
    pickup_note: "",
  });
  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    shipping_method: "ship_flat",
    shipping_address: "",
    payment_method: "etransfer",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  // Auto-fill from logged-in customer
  useEffect(() => {
    if (isCustomer && user) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
        shipping_address: f.shipping_address || user.shipping_address || "",
      }));
    }
  }, [isCustomer, user]);

  const shippingCost = useMemo(() => {
    if (form.shipping_method === "pickup" && settings.pickup_available) return 0;
    return settings.flat_shipping_rate || 15.0;
  }, [form.shipping_method, settings]);

  const total = useMemo(
    () => Number((subtotal + shippingCost).toFixed(2)),
    [subtotal, shippingCost]
  );

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canAdvanceShipping = () => {
    if (!form.customer_name.trim() || !form.email.trim() || !form.phone.trim())
      return false;
    if (form.shipping_method === "ship_flat" && !form.shipping_address.trim())
      return false;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        items: items.map((it) => ({
          product_id: it.id,
          product_name: it.name,
          quantity: it.quantity || 1,
        })),
        shipping_method: form.shipping_method,
        needs_shipping: form.shipping_method === "ship_flat",
        shipping_address:
          form.shipping_method === "ship_flat" ? form.shipping_address.trim() : "",
        payment_method: form.payment_method,
        fulfillment: "ship_sealed",
        message: form.message.trim(),
      };
      const { data } = await api.post("/orders", payload);
      setDone(data);
      clear();
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Something went wrong — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Confirmation screen ───
  if (done) {
    return <Confirmation order={done} />;
  }

  // ─── Empty cart ───
  if (items.length === 0) {
    return (
      <div
        data-testid="checkout-empty"
        className="max-w-xl mx-auto px-4 sm:px-8 py-24 text-center"
      >
        <ShoppingCart className="w-16 h-16 text-slate-400 mx-auto" />
        <h1 className="mt-6 font-[Outfit] text-4xl font-black">Your cart is empty.</h1>
        <p className="mt-4 text-slate-600">
          Browse the shop to find sealed product, singles & slabs.
        </p>
        <Link
          to="/shop"
          data-testid="checkout-browse-shop"
          className="mt-8 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-3 rounded-md uppercase tracking-wide text-sm"
        >
          Browse the shop <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid="checkout-page"
      className="max-w-5xl mx-auto px-4 sm:px-8 py-16"
    >
      <div className="text-xs tracking-[0.25em] uppercase font-mono text-red-600 mb-3">
        // Checkout
      </div>
      <h1 className="font-[Outfit] text-4xl sm:text-5xl font-black tracking-tighter">
        Finish your order.
      </h1>

      <Stepper step={step} />

      {error && (
        <div
          data-testid="checkout-error"
          className="mt-6 bg-red-500/10 border border-red-500/40 rounded-lg p-4 flex items-start gap-3 text-sm text-red-200"
        >
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
        <div>
          {step === "cart" && (
            <CartStep
              items={items}
              setQuantity={setQuantity}
              remove={remove}
              onNext={() => setStep("shipping")}
            />
          )}
          {step === "shipping" && (
            <ShippingStep
              form={form}
              setField={setField}
              settings={settings}
              onBack={() => setStep("cart")}
              onNext={() => setStep("payment")}
              canAdvance={canAdvanceShipping()}
            />
          )}
          {step === "payment" && (
            <PaymentStep
              form={form}
              setField={setField}
              onBack={() => setStep("shipping")}
              onSubmit={submit}
              submitting={submitting}
              total={total}
            />
          )}
        </div>

        <OrderSummary
          items={items}
          subtotal={subtotal}
          shippingCost={shippingCost}
          total={total}
          shippingMethod={form.shipping_method}
          pickupAvailable={settings.pickup_available}
        />
      </div>
    </div>
  );
}

/* ────────── Stepper ────────── */
function Stepper({ step }) {
  const idx = STEPS.indexOf(step);
  return (
    <div
      data-testid="checkout-stepper"
      className="mt-8 flex items-center gap-2 sm:gap-4"
    >
      {STEPS.map((s, i) => {
        const active = i <= idx;
        const labels = { cart: "Cart", shipping: "Shipping", payment: "Payment" };
        return (
          <React.Fragment key={s}>
            <div
              className={`flex items-center gap-2 text-xs sm:text-sm font-mono uppercase tracking-wider ${
                active ? "text-red-600" : "text-slate-400"
              }`}
            >
              <span
                className={`w-6 h-6 flex items-center justify-center rounded-full border font-bold ${
                  active
                    ? "bg-red-600 text-white border-red-600"
                    : "bg-white text-slate-400 border-slate-300"
                }`}
              >
                {i + 1}
              </span>
              <span className="hidden sm:inline">{labels[s]}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={`flex-1 h-px ${active && i < idx ? "bg-red-600" : "bg-slate-300"}`}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ────────── Step 1: Cart ────────── */
function CartStep({ items, setQuantity, remove, onNext }) {
  return (
    <div data-testid="step-cart" className="bg-white border border-slate-200 rounded-2xl p-6">
      <h2 className="font-[Outfit] text-2xl font-black">Review your cart</h2>
      <div className="mt-6 divide-y divide-slate-200">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`cart-row-${it.id}`}
            className="py-4 flex gap-4 items-center"
          >
            <div className="w-16 h-20 bg-slate-100 rounded overflow-hidden shrink-0">
              {it.image_url ? (
                <img
                  src={it.image_url}
                  alt={it.name}
                  className="w-full h-full object-cover"
                />
              ) : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-slate-900 truncate">{it.name}</div>
              <div className="text-xs text-slate-500 font-mono uppercase tracking-wider mt-1">
                {it.source === "vault" ? "Single / Slab" : "Sealed"}
              </div>
              <div className="mt-2 flex items-center gap-3">
                {it.source === "vault" ? (
                  <span className="text-xs text-slate-500">Qty 1 (unique item)</span>
                ) : (
                  <>
                    <label className="text-xs text-slate-500 font-mono uppercase tracking-wider">
                      Qty
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={it.quantity || 1}
                      onChange={(e) => setQuantity(it.id, e.target.value)}
                      data-testid={`cart-qty-${it.id}`}
                      className="w-16 bg-white border border-slate-300 rounded-md px-2 py-1 text-sm text-slate-900 focus:outline-none focus:border-red-500"
                    />
                  </>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="font-mono text-xl font-bold text-red-600">
                ${((it.price || 0) * (it.quantity || 1)).toFixed(2)}
              </div>
              <button
                type="button"
                onClick={() => remove(it.id)}
                data-testid={`cart-remove-${it.id}`}
                className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-between items-center">
        <Link
          to="/shop"
          data-testid="cart-continue-shopping"
          className="text-sm text-slate-500 hover:text-slate-700 font-mono uppercase tracking-wider"
        >
          ← Continue shopping
        </Link>
        <button
          type="button"
          onClick={onNext}
          data-testid="cart-next"
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-3 rounded-md uppercase tracking-wide text-sm"
        >
          Continue to shipping <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ────────── Step 2: Shipping ────────── */
function ShippingStep({ form, setField, settings, onBack, onNext, canAdvance }) {
  return (
    <div
      data-testid="step-shipping"
      className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6"
    >
      <h2 className="font-[Outfit] text-2xl font-black">Where should it go?</h2>

      <section className="space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
          Your info
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TextInput
            label="Full name"
            value={form.customer_name}
            onChange={(v) => setField("customer_name", v)}
            testid="checkout-name"
          />
          <TextInput
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => setField("email", v)}
            testid="checkout-email"
          />
          <TextInput
            label="Phone"
            value={form.phone}
            onChange={(v) => setField("phone", v)}
            testid="checkout-phone"
          />
        </div>
      </section>

      <section className="space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
          Delivery method
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ShippingOption
            selected={form.shipping_method === "ship_flat"}
            onClick={() => setField("shipping_method", "ship_flat")}
            icon={Truck}
            title={`Tracked shipping · $${(settings.flat_shipping_rate || 15).toFixed(2)}`}
            subtitle="Flat rate across Canada — Canada Post tracked parcel."
            testid="shipping-ship-flat"
          />
          {settings.pickup_available && (
            <ShippingOption
              selected={form.shipping_method === "pickup"}
              onClick={() => setField("shipping_method", "pickup")}
              icon={MapPin}
              title="Local pickup · Free"
              subtitle={
                settings.pickup_note ||
                "Meet us at the next show in Alberta — no shipping fee."
              }
              testid="shipping-pickup"
            />
          )}
        </div>
      </section>

      {form.shipping_method === "ship_flat" && (
        <section className="space-y-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
            Shipping address
          </div>
          <textarea
            data-testid="checkout-address"
            rows={3}
            value={form.shipping_address}
            onChange={(e) => setField("shipping_address", e.target.value)}
            placeholder="Street, City, Province, Postal Code"
            className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-red-500"
          />
        </section>
      )}

      <section className="space-y-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
          Add a note (optional)
        </div>
        <textarea
          data-testid="checkout-message"
          rows={2}
          value={form.message}
          onChange={(e) => setField("message", e.target.value)}
          placeholder="Anything we should know?"
          className="w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-red-500"
        />
      </section>

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onBack}
          data-testid="shipping-back"
          className="text-sm text-slate-500 hover:text-slate-700 font-mono uppercase tracking-wider"
        >
          ← Back to cart
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canAdvance}
          data-testid="shipping-next"
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-bold px-5 py-3 rounded-md uppercase tracking-wide text-sm"
        >
          Continue to payment <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ────────── Step 3: Payment ────────── */
function PaymentStep({ form, setField, onBack, onSubmit, submitting, total }) {
  return (
    <div
      data-testid="step-payment"
      className="bg-white border border-slate-200 rounded-2xl p-6 space-y-6"
    >
      <h2 className="font-[Outfit] text-2xl font-black">How are you paying?</h2>

      <div className="space-y-3">
        <PaymentOption
          selected={form.payment_method === "etransfer"}
          onClick={() => setField("payment_method", "etransfer")}
          icon={Mail}
          title="Interac e-Transfer"
          subtitle={`Send to ${PAYMENT_EMAIL} once we confirm your total by email.`}
          testid="payment-etransfer"
        />
        <PaymentOption
          selected={form.payment_method === "paypal"}
          onClick={() => setField("payment_method", "paypal")}
          icon={Mail}
          title="PayPal"
          subtitle={`Send to ${PAYMENT_EMAIL} as Friends & Family (goods & services if you prefer buyer protection — fees apply).`}
          testid="payment-paypal"
        />
        <PaymentOption
          selected={false}
          onClick={() => {}}
          icon={CreditCard}
          title="Credit / Debit card (Square)"
          subtitle="Coming soon — card checkout is being wired up."
          disabled
          testid="payment-square"
        />
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-600 flex gap-2">
        <Lock className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" />
        <span>
          Placing this order sends your details to us — we&apos;ll email you to
          confirm your total and lock in payment. No charge is made at this
          step.
        </span>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <button
          type="button"
          onClick={onBack}
          data-testid="payment-back"
          className="text-sm text-slate-500 hover:text-slate-700 font-mono uppercase tracking-wider"
        >
          ← Back to shipping
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          data-testid="payment-submit"
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-400 disabled:cursor-not-allowed text-white font-bold px-5 py-3 rounded-md uppercase tracking-wide text-sm"
        >
          {submitting ? "Placing order…" : `Place order · $${total.toFixed(2)}`}
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ────────── Order summary (sidebar) ────────── */
function OrderSummary({
  items,
  subtotal,
  shippingCost,
  total,
  shippingMethod,
  pickupAvailable,
}) {
  return (
    <aside
      data-testid="order-summary"
      className="bg-white border border-slate-200 rounded-2xl p-6 sticky top-24"
    >
      <h3 className="font-[Outfit] text-lg font-black">Order summary</h3>
      <ul className="mt-4 space-y-2 text-sm text-slate-700">
        {items.map((it) => (
          <li key={it.id} className="flex justify-between gap-3">
            <span className="truncate">
              {it.name}
              {(it.quantity || 1) > 1 ? ` × ${it.quantity}` : ""}
            </span>
            <span className="font-mono shrink-0">
              ${((it.price || 0) * (it.quantity || 1)).toFixed(2)}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 pt-4 border-t border-slate-200 space-y-2 text-sm">
        <div className="flex justify-between text-slate-600">
          <span>Subtotal</span>
          <span className="font-mono">${subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-slate-600">
          <span>
            Shipping{" "}
            {shippingMethod === "pickup" && pickupAvailable
              ? "(pickup)"
              : "(flat tracked)"}
          </span>
          <span className="font-mono">
            {shippingCost === 0 ? "FREE" : `$${shippingCost.toFixed(2)}`}
          </span>
        </div>
        <div className="flex justify-between pt-2 border-t border-slate-200 font-bold text-slate-900">
          <span>Total</span>
          <span className="font-mono text-xl text-red-600">
            ${total.toFixed(2)}
          </span>
        </div>
      </div>
    </aside>
  );
}

/* ────────── Confirmation screen ────────── */
function Confirmation({ order }) {
  return (
    <div
      data-testid="checkout-done"
      className="max-w-2xl mx-auto px-4 sm:px-8 py-24 text-center"
    >
      <CheckCircle2 className="w-16 h-16 text-red-600 mx-auto" />
      <h1 className="mt-6 font-[Outfit] text-4xl font-black">Order placed!</h1>
      <p className="mt-4 text-slate-600">
        Thanks{" "}
        <span className="text-slate-900 font-semibold">{order.customer_name}</span>{" "}
        — we&apos;ll email{" "}
        <span className="text-slate-900">{order.email}</span> to confirm your
        total and payment details shortly.
      </p>

      <div className="mt-8 bg-white border border-slate-200 rounded-xl p-6 text-left">
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-3">
          Order summary
        </div>
        <ul className="space-y-2">
          {(order.items || []).map((it, i) => (
            <li key={i} className="flex justify-between text-sm">
              <span className="text-slate-900 font-semibold">{it.product_name}</span>
              <span className="text-slate-500 font-mono">× {it.quantity}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-600 flex justify-between">
          <span>
            {order.shipping_method === "pickup"
              ? "Local pickup — free"
              : `Tracked shipping — $${(order.shipping_cost || 0).toFixed(2)}`}
          </span>
          <span className="font-mono font-bold text-slate-900">
            Total ${Number(order.total || 0).toFixed(2)}
          </span>
        </div>
        <div className="mt-2 text-xs text-slate-500 font-mono">
          Order ID: {order.id}
        </div>
      </div>

      <div className="mt-4 bg-white border border-red-500/30 rounded-xl p-6 text-left">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-red-600 mb-2">
          Payment info — wait for confirmation
        </div>
        <p className="text-sm text-slate-700 leading-relaxed">
          Once we confirm your order by email, send your{" "}
          <span className="font-bold">
            {order.payment_method === "etransfer" ? "Interac e-Transfer" : "PayPal"}
          </span>{" "}
          to:
        </p>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <code className="bg-slate-100 border border-slate-200 px-3 py-2 rounded text-red-600 font-mono text-sm break-all">
            {PAYMENT_EMAIL}
          </code>
          <button
            type="button"
            data-testid="checkout-copy-email"
            onClick={() => navigator.clipboard?.writeText(PAYMENT_EMAIL)}
            className="bg-red-600 hover:bg-red-700 text-white font-bold px-3 py-2 rounded-md text-xs uppercase tracking-wide"
          >
            Copy
          </button>
        </div>
      </div>

      <Link
        to={`/order-status/${order.id}`}
        data-testid="checkout-track"
        className="mt-8 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-3 rounded-md uppercase tracking-wide text-sm"
      >
        View order status <ArrowRight className="w-4 h-4" />
      </Link>
      <p className="mt-3 text-xs text-slate-500">
        Bookmark that link — your confirmation will appear there as soon as we
        reply.
      </p>
      <Link
        to="/"
        className="mt-6 block text-red-600 hover:text-red-700 font-mono text-sm uppercase tracking-wider"
      >
        ← Back home
      </Link>
    </div>
  );
}

/* ────────── Small UI helpers ────────── */
function TextInput({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-slate-500">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1 w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-red-500"
      />
    </label>
  );
}

function ShippingOption({ selected, onClick, icon: Icon, title, subtitle, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left p-4 rounded-xl border-2 transition-all ${
        selected
          ? "border-red-600 bg-red-50"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={`w-5 h-5 shrink-0 mt-0.5 ${selected ? "text-red-600" : "text-slate-500"}`}
        />
        <div>
          <div className="font-bold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
        </div>
      </div>
    </button>
  );
}

function PaymentOption({
  selected,
  onClick,
  icon: Icon,
  title,
  subtitle,
  disabled,
  testid,
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
        selected
          ? "border-red-600 bg-red-50"
          : disabled
            ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
            : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={`w-5 h-5 shrink-0 mt-0.5 ${selected ? "text-red-600" : "text-slate-500"}`}
        />
        <div>
          <div className="font-bold text-slate-900 flex items-center gap-2">
            {title}
            {disabled && (
              <span className="text-[10px] font-mono uppercase tracking-wider bg-slate-200 text-slate-500 px-2 py-0.5 rounded">
                Soon
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">{subtitle}</div>
        </div>
      </div>
    </button>
  );
}
